import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './CardObra.css';
import { apiService } from '../../services/ApiService';
import { useAuth } from '../../contexts/AuthContext';

const CardObra = ({ obra, onExcluir, darkMode, onUpdate, onEdit }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [modalStatusAberto, setModalStatusAberto] = useState(false);
  const [statusEditado, setStatusEditado] = useState(obra.status || 'Ativo');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);
  const [alteracoesNaoSalvas, setAlteracoesNaoSalvas] = useState(false);

  // Verificar se o usuário é um cliente
  const isClient = currentUser?.role === 'client';

  // Verificar se a obra é compartilhada da empresa
  const isObraCompartilhadaEmpresa = obra.compartilhada && !isClient;

  const formatarData = (data) => {
    if (!data) return 'Data não definida';
    const dataObj = new Date(data);
    // Adicionar um dia para compensar o fuso horário UTC
    dataObj.setDate(dataObj.getDate() + 1);
    return dataObj.toLocaleDateString('pt-BR');
  };

  const getStatusClass = () => {
    const status = obra.status?.toLowerCase() || 'ativo';
    return `status-badge ${status.replace(' ', '-')}`;
  };

  const getProgressoClass = () => {
    const progresso = obra.progresso || 0;
    if (progresso < 30) return 'progresso-baixo';
    if (progresso < 70) return 'progresso-medio';
    return 'progresso-alto';
  };

  const handleClick = () => {
    navigate(`/diario-obra/${obra.id}`);
  };
  
  const handleExcluir = (e) => {
    e.stopPropagation();
    
    if (window.confirm(`Tem certeza que deseja excluir a obra "${obra.nome}"?`)) {
      onExcluir && onExcluir(obra.id);
    }
  };

  const handleEditar = (e) => {
    e.stopPropagation();
    onEdit && onEdit(obra);
  };

  const handleEditarStatus = (e) => {
    e.stopPropagation();
    setStatusEditado(obra.status || 'Ativo');
    setModalStatusAberto(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setStatusEditado(value);
    setAlteracoesNaoSalvas(true);
  };

  const handleStatusChange = (e) => {
    setStatusEditado(e.target.value);
    setAlteracoesNaoSalvas(true);
  };

  const handleSalvarStatus = async (e) => {
    e.preventDefault();
    setSalvando(true);
    setErro(null);
    
    try {
      const dadosAtualizacao = { status: statusEditado };
      
      const resultado = await apiService.updateDiarioObra(obra.id, dadosAtualizacao);
      
      if (!resultado.success) {
        throw new Error(resultado.error || 'Erro ao atualizar status');
      }
      
      const obraAtualizada = { ...obra, status: statusEditado };
      
      if (onUpdate && typeof onUpdate === 'function') {
        onUpdate(resultado.data?.obra || obraAtualizada);
      }
      
      setAlteracoesNaoSalvas(false);
      setModalStatusAberto(false);
    } catch (error) {
      setErro(error.message || 'Não foi possível atualizar o status. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div 
      className={`card-obra ${darkMode ? 'dark-mode' : ''} ${isObraCompartilhadaEmpresa ? 'obra-compartilhada-empresa' : ''}`}
      onClick={handleClick}
    >
      {isObraCompartilhadaEmpresa && (
        <div className="obra-compartilhada-badge">
          <i className="fas fa-building"></i>
          <span>Compartilhada da Empresa</span>
        </div>
      )}
      
      <div className="obra-card-header">
        <h3>{obra.nome}</h3>
      </div>
      
      <div className="card-obra-body">
        <div className="obra-info">
          <div className="info-item">
            <label>Cliente:</label>
            <span>{obra.cliente || 'Não definido'}</span>
          </div>
          <div className="info-item">
            <label>Local:</label>
            <span>{obra.endereco || 'Não definido'}</span>
          </div>
          <div className="info-item">
            <label>Início:</label>
            <span>{formatarData(obra.dataInicio)}</span>
          </div>
          <div className="info-item">
            <label>Responsável:</label>
            <span>{obra.responsavel || 'Não definido'}</span>
          </div>
        </div>
        
        {typeof obra.progresso !== 'undefined' && (
        <div className="obra-progresso-container">
          <label>Progresso:</label>
          <div className="obra-progresso-barra">
            <div 
              className={`obra-progresso-valor ${getProgressoClass()}`} 
              style={{ width: `${obra.progresso || 0}%` }}
            >
              <span>{obra.progresso || 0}%</span>
            </div>
          </div>
        </div>
        )}
      </div>
      
      <div className="card-obra-footer">
        <div className="card-obra-acoes">
          <button className="btn-ver-detalhes">
            Ver Detalhes <i className="fas fa-arrow-right"></i>
          </button>
          {!isClient && (
            <>
              <button 
                className="btn-editar" 
                onClick={handleEditar}
                title="Editar obra"
              >
                <i className="fas fa-edit"></i>
              </button>
              <button 
                className="btn-excluir" 
                onClick={handleExcluir}
                title="Excluir obra"
              >
                <i className="fas fa-trash"></i>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Modal de status */}
      {!isClient && modalStatusAberto && (
        <div className="modal-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header">
              <h2>Alterar Status da Obra</h2>
              <button 
                className="btn-fechar" 
                onClick={() => setModalStatusAberto(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {erro && (
              <div className="erro-mensagem">
                <i className="fas fa-exclamation-triangle"></i> {erro}
              </div>
            )}

            <form className="form-editar-status" onSubmit={handleSalvarStatus}>
              <div className="form-group">
                <label htmlFor="status-select">Status da Obra</label>
                <select
                  id="status-select"
                  value={statusEditado}
                  onChange={handleStatusChange}
                  className="status-select"
                >
                  <option value="Ativo">Ativo</option>
                  <option value="Em andamento">Em andamento</option>
                  <option value="Concluída">Concluída</option>
                  <option value="Pausada">Pausada</option>
                  <option value="Cancelada">Cancelada</option>
                </select>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-cancelar"
                  onClick={() => setModalStatusAberto(false)}
                  disabled={salvando}
                >
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  className={`btn-salvar ${alteracoesNaoSalvas ? 'dashboard-action-button unsaved-changes' : ''}`}
                  disabled={salvando}
                >
                  {salvando ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Salvando...
                    </>
                  ) : 'Atualizar Status'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CardObra; 